import { XIcon } from '@heroicons/react/outline';
import { Form, Input, message, Modal, Rate, UploadFile } from 'antd';
import useBreakpoint from 'antd/lib/grid/hooks/useBreakpoint';
import BadWordsNext from 'bad-words-next';
import en from 'bad-words-next/data/en.json';
import Image from 'next/image';
import { useRouter } from 'next/router';
import React, { useEffect, useState } from 'react';
import { useRecoilState, useRecoilValue } from 'recoil';

import { IReviewsData } from '..';
import LogoutIcon from '../../../../../assets/Icons/LogoutIcon';
import { authModalControl } from '../../../../atoms/authModalControl';
import { isRatingRefreshAtom } from '../../../../atoms/isRatingRefreshAtom';
import { user } from '../../../../atoms/user';
import {
  createCourseReview,
  updateCourseReview,
} from '../../../../services/ReviewLandingPage';
import { User } from '../../../../utils/interfaces/User';
import ButtonDefault, { ButtonVariants } from '../../../shared/basic/button';
import { ShouldRender } from '../../../shared/basic/ShouldRender';
import { formateErrorObject } from '../../../shared/formateErrorObject';
import { notify } from '../../../shared/notify';

const filterWords = new BadWordsNext({ data: en });

interface CreateReviewModalProps {
  isModalOpen: boolean;
  handleClose: () => void;
  type: 'create' | 'edit';
  review?: IReviewsData | undefined;
  fetchData: () => Promise<void>;
  isLoggedIn: boolean;
  isPurchased: boolean;
  productId: string;
  courseImage: string;
  courseName: string;
  authorName: string;
}

const TIMEOUT_DURATION = 3 * 60 * 1000;

interface FormValues {
  rating: number;
  review: string;
}

const CreateCourseReview: React.FC<CreateReviewModalProps> = ({
  isModalOpen,
  handleClose,
  review,
  fetchData,
  isLoggedIn,
  isPurchased,
  productId,
  courseImage,
  courseName,
  authorName,
}) => {
  const [loading, setLoading] = useState<boolean>(false);
  const [form] = Form.useForm<FormValues>();
  const userData: User | undefined = useRecoilValue(user);
  const [deletedImage, setDeletedImage] = useState<string[]>([]);
  const [, setIsRatingRefresh] = useRecoilState(isRatingRefreshAtom);
  const [showLoginOverlay, setShowLoginOverlay] = useState<boolean>(false);
  const [, setVisible] = useRecoilState(authModalControl);
  const router = useRouter();
  const screens = useBreakpoint();

  const [formData, setFormData] = useState({
    rating: 0,
    review: '',
    images: [] as UploadFile[],
  });

  const saveFormDataToLocalStorage = (data: typeof formData) => {
    if (typeof localStorage === 'undefined') {
      return;
    }
    localStorage.setItem('reviewFormData', JSON.stringify(data));
    localStorage.setItem('reviewFormTimestamp', Date.now().toString());
  };

  useEffect(() => {
    if (typeof localStorage === 'undefined') {
      return;
    }
    const savedData = localStorage.getItem('reviewFormData');
    const savedTimestamp = localStorage.getItem('reviewFormTimestamp');
    const isWithinTimeout =
      savedTimestamp &&
      Date.now() - parseInt(savedTimestamp) < TIMEOUT_DURATION;

    if (savedData && isWithinTimeout && !review && isModalOpen) {
      setFormData(JSON.parse(savedData));
      setFormData((prev) => ({
        ...prev,
        rating: JSON.parse(savedData).rating,
        review: JSON.parse(savedData).review,
      }));

      form.setFieldsValue({
        rating: JSON.parse(savedData).rating,
        review: JSON.parse(savedData).review,
      });
    }
  }, [review, isModalOpen]);

  useEffect(() => {
    if (
      formData.review ||
      formData.rating ||
      (formData.images && formData.images.length > 0)
    ) {
      saveFormDataToLocalStorage(formData);
    }
  }, [formData]);

  const handleInputChange = (changedValues: any) => {
    setFormData((prev) => {
      const updatedData = { ...prev, ...changedValues };

      if ('review' in changedValues) {
        updatedData.review = changedValues.review
          ? filterWords.filter(changedValues.review)
          : '';
      }

      return updatedData;
    });
  };

  const handleSubmit = async () => {
    const { rating, review: reviewText, images } = formData;
    const { name, zl_uid, avatar } = userData || {};

    if (!productId || (!review && !reviewText) || !rating) {
      message.error('Review and rating are required.');
      return;
    }

    setLoading(true);
    if (!isLoggedIn) {
      setShowLoginOverlay(true);
      setLoading(false);
      return;
    }
    try {
      const mappedImages = images.map((file) => file.url || file.originFileObj);
      const reviewData = {
        rating,
        title: 'to-do',
        text: reviewText,
        images: mappedImages,
        userName: name as string,
        userId: zl_uid as string,
        type: 'course',
        ...(avatar && { userImage: avatar as string }),
      };

      if (!isPurchased) {
        notify('You need to purchase this product to submit a review!', 'info');
        handleCancel();
        setLoading(false);
        return;
      }
      if (review) {
        const updatedFields = {
          ...(rating !== review.rating && { rating }),
          ...(reviewText !== review.text && { text: reviewText }),
        };

        if (Object.keys(updatedFields).length > 0) {
          await updateCourseReview(
            updatedFields,
            review._id as string,
            deletedImage,
          );
        }
        setIsRatingRefresh(true);
        setDeletedImage([]);
      } else {
        await createCourseReview(reviewData, productId);
      }
      handleCancel();
      await fetchData();
    } catch (error: any) {
      notify(formateErrorObject(error), 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    handleClose();
    setFormData({
      rating: 0,
      review: '',
      images: [] as UploadFile[],
    });
    setDeletedImage([]);
    form.setFieldsValue({
      rating: 0,
      review: '',
    });
    handleCleanLocalStorage();
    const { isReview, ...restQueries } = router.query;

    router.push(
      {
        pathname: router.pathname,
        query: {
          ...restQueries,
        },
      },
      undefined,
      { shallow: true },
    );
  };

  const handleCleanLocalStorage = () => {
    if (typeof localStorage === 'undefined') {
      return;
    }
    localStorage.removeItem('reviewFormData');
    localStorage.removeItem('reviewFormTimestamp');
  };

  useEffect(() => {
    if (isLoggedIn) {
      setShowLoginOverlay(!isLoggedIn);
    }
  }, [isLoggedIn]);

  useEffect(() => {
    if (review) {
      setFormData((prev) => ({
        ...prev,
        rating: review.rating,
        review: review.text,
      }));

      form.setFieldsValue({
        rating: review.rating,
        review: review.text,
      });
    }
  }, [review, isModalOpen]);

  return (
    <Modal
      closable={false}
      centered
      open={isModalOpen}
      onCancel={handleCancel}
      footer={
        !showLoginOverlay ? (
          <div className='flex justify-end'>
            <div className='flex gap-x-3 pb-6  justify-end px-4 md:px-8 bg-opacity-70 backdrop-filter  md:backdrop-blur-none w-2/5'>
              <ButtonDefault
                size={4}
                rounded={false}
                roundedSm={true}
                onClick={(e) => {
                  e.stopPropagation();
                  e.preventDefault();
                  handleCancel();
                }}
                variant={ButtonVariants.UNSTYLED}
                className='border-[#282829] border'
              >
                <span className='flex text-sm gap-x-2  text-white  bg-transparent whitespace-nowrap'>
                  Cancel
                </span>
              </ButtonDefault>
              <ButtonDefault
                size={4}
                loading={loading}
                rounded={false}
                roundedSm={true}
                disabled={loading}
                onClick={handleSubmit}
                variant={ButtonVariants.PRIMARY}
              >
                <span className='flex text-sm gap-x-2 px-4 whitespace-nowrap'>
                  Submit
                </span>
              </ButtonDefault>
            </div>
          </div>
        ) : (
          false
        )
      }
      className='customLoginModal  bg-[#1C1C1C]  relative min-h-full md:min-h-min md:h-min md:rounded-2xl border-2 border-borderDarkGray'
      width={700}
      destroyOnHidden
    >
      {/* Modal Content */}
      <div className='flex flex-col min-h-full  justify-between h-full md:min-h-fit '>
        {/* Header Section */}
        <div className='flex justify-between  h-full items-center  md:rounded-t-2xl px-4 md:px-8 py-4 xl:py-6'>
          <div className='flex flex-col gap-1'>
            <span className='text-lg text-white font-semibold'>
              Write a Review
            </span>
          </div>
          <span className=' rounded-full p-1.5  flex justify-center items-center'>
            <XIcon
              onClick={handleCancel}
              className='w-5 h-5 text-white cursor-pointer'
            />
          </span>
        </div>

        {/* Body Section */}

        <div className='flex-grow px-7  pb-2 relative overflow-y-auto h-full'>
          <div className='flex items-center bg-[#121212]  p-4 rounded-lg w-full'>
            <Image
              src={courseImage}
              alt='Course Thumbnail'
              width={144}
              height={72}
              className=' rounded-lg object-cover'
            />
            <div className='ml-4 flex-1'>
              <h2 className='text-white text-lg font-semibold'>{courseName}</h2>
              <p className='text-gray-400 text-sm'>{`an online course by ${authorName}`}</p>
            </div>
            <button
              onClick={() => handleCancel()}
              className='text-primary text-sm hover:underline font-semibold'
            >
              View Details
            </button>
          </div>
          <Form
            form={form}
            layout='vertical'
            onFinish={handleSubmit}
            initialValues={{ rating: 0, review: '', images: [] }}
            onValuesChange={handleInputChange}
            className='pt-7 flex flex-col gap-y-6'
          >
            {/* Rating Input */}
            <Form.Item
              name='rating'
              label={
                <span className='text-xs 2xl:text-sm text-[#FFFFFF99] uppercase  font-semibold'>
                  Give Rating
                </span>
              }
              rules={[
                { required: true, message: 'Please provide a rating!' },
                {
                  validator: (_, value) =>
                    value && value > 0
                      ? Promise.resolve()
                      : Promise.reject('Please provide a rating!'),
                },
              ]}
            >
              <Rate
                style={{ fontSize: '24px' }}
                className='text-white ml-1'
                allowClear
                value={formData.rating}
              />
            </Form.Item>

            {/* Review Text Input */}
            <Form.Item
              name='review'
              rules={[{ required: true, message: 'Please write a review!' }]}
              label={
                <span className='text-xs 2xl:text-sm text-[#FFFFFF99] uppercase   font-semibold'>
                  Write a review
                </span>
              }
            >
              <Input.TextArea
                autoSize={{ minRows: screens.md ? 5 : 8, maxRows: 10 }}
                placeholder='Write your review, what did you like or dislike?'
                className='text-white bg-transparent text-[16px]'
                value={formData.review}
              />
            </Form.Item>
          </Form>
          <ShouldRender check={showLoginOverlay}>
            <div className='absolute inset-0 bg-backGroundReaderTabBg  backdrop-filter backdrop-blur-[1.5px]   bg-opacity-60 flex flex-col items-center justify-center z-10 text-white p-4 md:p-8'>
              <LogoutIcon />
              <h2 className='text-2xl mb-4 mt-14'>You’re not logged in</h2>
              <p className='text-center text-sm text-textGrayLight mb-12'>
                Please login to submit your review and help others find their
                next best read.
              </p>

              <div className='w-2/3'>
                <ButtonDefault
                  size={1}
                  variant={ButtonVariants.WHITE}
                  className='p-1 w-full'
                  disabled={loading}
                  loading={loading}
                  roundedSm={true}
                  rounded={false}
                  enableScaling={false}
                  onClick={() =>
                    setVisible((prevState) => ({
                      ...prevState,
                      login: true,
                    }))
                  }
                >
                  <span className='flex text-sm font-bold gap-x-2  py-1 whitespace-nowrap'>
                    Login
                  </span>
                </ButtonDefault>
              </div>
            </div>
          </ShouldRender>
        </div>
      </div>
    </Modal>
  );
};

export default CreateCourseReview;
